/**
 * Înregistrare + onboarding, într-un singur flux în trei pași (cerința #6):
 * cont → restaurant → confirmare. Server-ul creează totul într-o singură tranzacție
 * (cont + restaurant + locație + abonament de probă), deci nu există cont „pe jumătate".
 */
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AppShell.js';
import { Badge, Button, Field, Icon, InlineAlert, Input, Select, Switch, cx } from '../components/ui.js';
import { api } from '../lib/api.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';

const LOCALES = [
  { value: 'ro', label: 'Română' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
  { value: 'es', label: 'Español' },
];

const CURRENCIES = ['RON', 'EUR', 'USD', 'GBP'];

interface FormState {
  name: string;
  email: string;
  password: string;
  accountName: string;
  restaurantName: string;
  city: string;
  currency: string;
  locale: string;
  multiLocation: boolean;
  additionalLocations: string;
  acceptTerms: boolean;
}

const INITIAL: FormState = {
  name: '',
  email: '',
  password: '',
  accountName: '',
  restaurantName: '',
  city: '',
  currency: 'RON',
  locale: 'ro',
  multiLocation: false,
  additionalLocations: '',
  acceptTerms: false,
};

export function RegisterPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (session.status === 'authenticated') return <Navigate to="/app" replace />;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: [] }));
  }

  function validateStep(index: number): boolean {
    const next: Record<string, string[]> = {};
    if (index === 0) {
      if (form.name.trim().length < 2) next.name = ['Spune-ne cum te cheamă.'];
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) next.email = ['Adresa de email nu pare validă.'];
      if (form.password.length < 10) next.password = ['Parola trebuie să aibă minim 10 caractere.'];
    }
    if (index === 1) {
      if (form.accountName.trim().length < 2) next.accountName = ['Numele restaurantului este obligatoriu.'];
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function submit() {
    setError(null);
    if (!validateStep(0) || !validateStep(1)) {
      setStep(0);
      return;
    }
    setLoading(true);
    try {
      const additionalLocations = form.multiLocation
        ? form.additionalLocations
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((name) => ({ name }))
        : [];

      await api.post('/auth/register', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        accountName: form.accountName.trim(),
        ...(form.restaurantName.trim() ? { restaurantName: form.restaurantName.trim() } : {}),
        ...(form.city.trim() ? { city: form.city.trim() } : {}),
        currency: form.currency,
        locale: form.locale,
        multiLocation: form.multiLocation,
        ...(additionalLocations.length ? { additionalLocations } : {}),
        acceptTerms: form.acceptTerms,
      });

      // Serverul a deschis deja sesiunea (cookie HttpOnly) — reîmprospătăm starea.
      // Dacă mediul nu poate păstra cookie-uri (cadru încorporat, protecții de tracking), sesiunea
      // nu se confirmă: reluăm autentificarea cu aceleași credențiale, pe transportul cu token,
      // în loc să trimitem utilizatorul către un dashboard care îl respinge imediat.
      const confirmed = await session.refresh();
      if (!confirmed?.authenticated) {
        await session.login(form.email.trim().toLowerCase(), form.password);
      }
      navigate('/app', { replace: true });
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length) {
        setError('Verifică datele introduse.');
        setStep(0);
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }

  const steps = ['Contul tău', 'Restaurantul', 'Confirmare'];

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h1 className="font-display text-display-m text-ink-950">Creează contul restaurantului</h1>
            <p className="text-body-s text-ink-600">
              14 zile de probă, fără card. Configurezi meniul în mai puțin de 10 minute.
            </p>
          </div>

          <ol className="flex items-center gap-2" aria-label="Pași">
            {steps.map((label, index) => (
              <li key={label} className="flex flex-1 items-center gap-2">
                <button
                  type="button"
                  onClick={() => index < step && setStep(index)}
                  className={cx(
                    'flex items-center gap-2 text-caption',
                    index === step ? 'font-medium text-brand-700' : 'text-ink-400',
                  )}
                >
                  <span
                    className={cx(
                      'flex h-6 w-6 items-center justify-center rounded-full text-micro',
                      index < step
                        ? 'bg-brand-600 text-white'
                        : index === step
                          ? 'bg-brand-100 text-brand-700'
                          : 'bg-surface-3 text-ink-400',
                    )}
                  >
                    {index < step ? <Icon name="check" size={13} /> : index + 1}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </button>
                {index < steps.length - 1 ? <span className="h-px flex-1 bg-ink-200" /> : null}
              </li>
            ))}
          </ol>
        </div>

        {error ? (
          <div className="mb-4">
            <InlineAlert tone="danger">{error}</InlineAlert>
          </div>
        ) : null}

        {step === 0 ? (
          <form
            className="flex flex-col gap-4"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (validateStep(0)) setStep(1);
            }}
          >
            <Field label="Numele tău" required errors={errors.name}>
              {(props) => (
                <Input
                  {...props}
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="Andrei Ionescu"
                />
              )}
            </Field>
            <Field label="Email" required errors={errors.email} hint="Aici primești linkul de confirmare și notificările de securitate.">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="nume@restaurant.ro"
                />
              )}
            </Field>
            <Field
              label="Parolă"
              required
              errors={errors.password}
              hint="Minim 10 caractere. Recomandat: o propoziție ușor de reținut."
            >
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                />
              )}
            </Field>
            <Button type="submit" size="lg" className="w-full">
              Continuă
            </Button>
          </form>
        ) : null}

        {step === 1 ? (
          <form
            className="flex flex-col gap-4"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (validateStep(1)) setStep(2);
            }}
          >
            <Field label="Numele restaurantului" required errors={errors.accountName} hint="Apare pe meniul public și pe ecrane.">
              {(props) => (
                <Input
                  {...props}
                  value={form.accountName}
                  onChange={(e) => update('accountName', e.target.value)}
                  placeholder="Pizzeria Roma"
                />
              )}
            </Field>
            <Field label="Oraș" errors={errors.city} hint="Folosim orașul ca nume implicit pentru prima locație.">
              {(props) => (
                <Input {...props} value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Cluj-Napoca" />
              )}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Monedă" errors={errors.currency}>
                {(props) => (
                  <Select {...props} value={form.currency} onChange={(e) => update('currency', e.target.value)}>
                    {CURRENCIES.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Limba meniului" errors={errors.locale}>
                {(props) => (
                  <Select {...props} value={form.locale} onChange={(e) => update('locale', e.target.value)}>
                    {LOCALES.map((locale) => (
                      <option key={locale.value} value={locale.value}>
                        {locale.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>

            <div className="rounded-[var(--radius-md)] border border-ink-100 p-4">
              <Switch
                checked={form.multiLocation}
                onChange={(value) => update('multiLocation', value)}
                label="Am mai multe locații"
                description="Creezi un grup de restaurante cu meniu comun, dar locații și QR-uri separate."
              />
              {form.multiLocation ? (
                <div className="mt-4">
                  <Field label="Locații suplimentare" hint="Câte una pe linie. Le poți redenumi oricând.">
                    {(props) => (
                      <textarea
                        {...props}
                        rows={3}
                        value={form.additionalLocations}
                        onChange={(e) => update('additionalLocations', e.target.value)}
                        placeholder={'Brașov\nConstanța'}
                        className="w-full rounded-[var(--radius-md)] border border-ink-200 bg-surface px-3.5 py-2.5 text-body"
                      />
                    )}
                  </Field>
                </div>
              ) : null}
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="secondary" size="lg" onClick={() => setStep(0)}>
                Înapoi
              </Button>
              <Button type="submit" size="lg" className="flex-1">
                Continuă
              </Button>
            </div>
          </form>
        ) : null}

        {step === 2 ? (
          <div className="flex flex-col gap-5">
            <dl className="flex flex-col divide-y divide-ink-100 rounded-[var(--radius-md)] border border-ink-100">
              <SummaryRow label="Cont" value={form.name} />
              <SummaryRow label="Email" value={form.email} mono />
              <SummaryRow label="Restaurant" value={form.accountName} />
              {form.city ? <SummaryRow label="Oraș" value={form.city} /> : null}
              <SummaryRow label="Monedă / limbă" value={`${form.currency} · ${form.locale.toUpperCase()}`} />
              {form.multiLocation ? (
                <SummaryRow label="Locații" value={`1 + ${form.additionalLocations.split('\n').filter((l) => l.trim()).length}`} />
              ) : null}
            </dl>

            <div className="flex items-start gap-3 rounded-[var(--radius-md)] bg-surface-3/70 p-4">
              <Badge tone="brand">14 zile gratuit</Badge>
              <p className="text-caption text-ink-600">
                Nu cerem card pentru perioada de probă. Poți schimba planul oricând din setările contului.
              </p>
            </div>

            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={form.acceptTerms}
                onChange={(e) => update('acceptTerms', e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-ink-200 text-brand-600 focus:ring-brand-600"
              />
              <span className="text-body-s text-ink-600">
                Accept termenii de utilizare și politica de confidențialitate. Prelucrăm doar datele necesare
                funcționării contului.
              </span>
            </label>
            {errors.acceptTerms ? <p className="text-caption text-danger">{errors.acceptTerms[0]}</p> : null}

            <div className="flex gap-2">
              <Button type="button" variant="secondary" size="lg" onClick={() => setStep(1)} disabled={loading}>
                Înapoi
              </Button>
              <Button
                type="button"
                size="lg"
                className="flex-1"
                loading={loading}
                disabled={!form.acceptTerms}
                onClick={() => void submit()}
              >
                Creează contul
              </Button>
            </div>
          </div>
        ) : null}

        <p className="mt-6 text-body-s text-ink-600">
          Ai deja cont?{' '}
          <Link to="/login" className="transition-ui font-medium text-brand-600 hover:text-brand-700">
            Autentifică-te
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

function SummaryRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <dt className="text-body-s text-ink-600">{label}</dt>
      <dd className={cx('truncate text-body-s font-medium text-ink-950', mono && 'font-mono text-caption')}>{value}</dd>
    </div>
  );
}
